import { GraphNode } from '../graph-node.js';
import { reportGraphError } from '../errors.js';
/**
 * Demand-evaluated value node (reverse data tree / pull data flow).
 * Output cache lives only on cache-points (feeds a non-value / fan-out)
 * or compile-folded sinks — not on every math node.
 */
export class ValueNode extends GraphNode {
    cachedOutputs = null;
    dirty = true;
    folded = false;
    isEvaluating = false;
    isVolatile;
    implementation;
    constructor(id, data, type, implementation, isVolatile) {
        super(id, data, type);
        this.implementation = implementation;
        this.isVolatile = isVolatile;
    }
    /**
     * Own a cache when this node feeds a non-value consumer or fans out.
     * Intermediate single-consumer value→value nodes recompute as part of the sink pull.
     */
    isCachePoint() {
        if (this.isVolatile)
            return false;
        let consumerCount = 0;
        let feedsNonValue = false;
        for (const connections of this.outConnections.values()) {
            for (const connection of connections) {
                consumerCount += 1;
                if (!(connection.connectedNode instanceof ValueNode)) {
                    feedsNonValue = true;
                }
            }
        }
        if (feedsNonValue)
            return true;
        return consumerCount > 1;
    }
    /** Cached outputs no longer match the inputs once an upstream value changed. */
    refreshAfterUpstreamValueChange() {
        this.markDirty();
    }
    markDirty() {
        if (this.folded && !this.isVolatile)
            return;
        if (this.dirty && this.cachedOutputs === null) {
            this.propagateDirty();
            return;
        }
        this.dirty = true;
        this.cachedOutputs = null;
        this.propagateDirty();
    }
    markFolded(outputs) {
        if (this.isVolatile)
            return;
        this.folded = true;
        this.dirty = false;
        this.cachedOutputs = { ...outputs };
        for (const [key, value] of Object.entries(outputs)) {
            this.data[key] = value;
        }
    }
    clearFolded() {
        this.folded = false;
    }
    isFolded() {
        return this.folded;
    }
    /** True when outputs must not be cached or constant-folded (e.g. random, scene lookup). */
    isVolatileNode() {
        return this.isVolatile;
    }
    getOutPortData(outPortKey) {
        if (this.isVolatile) {
            this.evaluateNow();
            return this.readPortValue(outPortKey);
        }
        if (this.folded && this.cachedOutputs) {
            return this.readPortValue(outPortKey);
        }
        if (!this.isCachePoint()) {
            this.evaluateNow();
            const value = this.readPortValue(outPortKey);
            this.discardTransientCache();
            return value;
        }
        if (this.dirty || !this.cachedOutputs) {
            this.evaluateNow();
        }
        return this.readPortValue(outPortKey);
    }
    /** Forces evaluation and returns all outputs (used by constant-fold pass). */
    evaluateAll() {
        this.evaluateNow();
        const outputs = { ...(this.cachedOutputs ?? {}) };
        if (!this.isCachePoint() && !this.folded) {
            this.discardTransientCache();
        }
        return outputs;
    }
    discardTransientCache() {
        this.cachedOutputs = null;
        this.dirty = true;
    }
    readPortValue(outPortKey) {
        if (this.cachedOutputs && Object.hasOwn(this.cachedOutputs, outPortKey)) {
            return this.cachedOutputs[outPortKey];
        }
        if (Object.hasOwn(this.data, outPortKey)) {
            return this.data[outPortKey];
        }
        console.error(`Node ${this.id} has no data for out port ${outPortKey}`);
        return undefined;
    }
    evaluateNow() {
        if (this.isEvaluating) {
            reportGraphError({
                kind: 'exec',
                nodeType: this.type,
                nodeId: this.id,
                summary: 'value evaluation cycle detected - reverse tree has a loop',
            });
            this.cachedOutputs = {};
            this.dirty = false;
            return;
        }
        this.isEvaluating = true;
        try {
            this.getDataFromInPorts();
            if (!this.implementation) {
                this.cachedOutputs = { ...this.data };
                this.dirty = false;
                return;
            }
            const outputs = this.implementation(this.data);
            this.cachedOutputs = outputs;
            for (const [key, value] of Object.entries(outputs)) {
                this.data[key] = value;
            }
            this.dirty = false;
        }
        finally {
            this.isEvaluating = false;
        }
    }
    propagateDirty() {
        for (const connections of this.outConnections.values()) {
            for (const connection of connections)
                connection.connectedNode.markDirty();
        }
    }
}
